import React, {useState} from 'react';
import {Box, Typography, Grid, Tabs, Tab, Tooltip} from '@mui/material';
import ClassDashboardShell from '../components/ClassDashboard.comp';
import NoDataAvailable from '../../../components/NoDataAvailable';
import CircularLoader from '../../../components/CircularLoader';
import {useDashboard} from '../../../context/DashboardContext';
import {isSuppressedItem, SUPPRESSED_COPY} from '../../DashboardCommon/dashboardCards';
import {SuppressedValue} from '../../DashboardCommon/SuppressedValue.comp';
import {READINESS_BAND_COLORS, MASTERY_BAND_COLOR} from '../constants';

import {BandChip} from '../../DashboardCommon/widgets.comp';
import {UNIT, fmtValue, benchmarkText} from '../../DashboardCommon/units';
import {SortToggle} from '../../DashboardCommon/SortToggle.comp';
import {sortItems, sortBars} from '../../DashboardCommon/barSort';
const NAVY   = '#052F44';
const ORANGE = '#FF6900';
const MARKER_SECONDARY = 'var(--brand-global-2, #7B8FC7)';
const MARKER_PRIMARY = 'var(--brand-text, #052F44)';
const primaryMarkerSx = (value) => ({
    position: 'absolute', left: `${value}%`, top: '-9px',
    width: 0, height: 0,
    borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
    borderTop: `9px solid ${MARKER_PRIMARY}`,
    transform: 'translateX(-50%)', cursor: 'pointer', zIndex: 4,
});
const secondaryMarkerSx = (value) => ({
    position: 'absolute', left: `${value}%`, bottom: '-9px',
    width: 0, height: 0,
    borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
    borderBottom: `9px solid ${MARKER_SECONDARY}`,
    transform: 'translateX(-50%)', cursor: 'pointer', zIndex: 4,
});

// Legend for the bar colors used below — every row's bar is colored by its
// own band (READINESS_BAND_COLORS), not one fixed "Class score" swatch, so
// the legend should show all the bands that actually appear in the chart
// rather than a single color that doesn't match most of the rows.
const READINESS_BAND_ORDER = ['High', 'Moderate', 'Basic', 'Low'];
const BandLegendChips = () => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
        {READINESS_BAND_ORDER.map((band) => (
            <BandChip key={band} label={band} color={READINESS_BAND_COLORS[band]}/>
        ))}
    </Box>
);

/* ════════════════════════════════════════════════════════════════ */
/*  TAB 1 — ITEM PROFILE (slide 3)                                  */
/* ════════════════════════════════════════════════════════════════ */

/* Canonical 8 readiness areas, in fixed display order — matches School
   Dashboard's Readiness Item Profile. S_3's item list also carries the 4
   Context & Support areas mixed in; this whitelist is what filters those
   out and pins the remaining areas to a stable order instead of ranking
   them by score. */
const READINESS_AREA_NAMES = [
    'Youth AI-Digital Activities',
    'Self-Regulated AI-Digital Engagement',
    'Digital Hygiene and Privacy Management',
    'Cyberbullying Management',
    'Content Risk Avoidance',
    'Contact Risk Avoidance',
    'Authentic and Ethical Digital Identity',
    'Critical Information Management',
];


// An item under the n floor (suppressed) keeps its label and shows the placeholder in place of bar, score and band.
const ItemRow = ({itemLabel, name, score, band, gradeAvg, schoolAvg, suppressed}) => {
    const rowColor = READINESS_BAND_COLORS[band] || MASTERY_BAND_COLOR[band] || READINESS_BAND_COLORS.Moderate;
    return (
        <Box sx={{
            display: 'grid', gridTemplateColumns: '220px 1fr 40px 100px', alignItems: 'center', gap: 1.5,
            py: 1.1, pl: 4, borderBottom: '1px solid #F3F4F6', '&:last-child': {borderBottom: 'none'},
        }}>
            <Box>
                <Typography sx={{fontSize: '12px', color: '#374151', lineHeight: 1.3}}>
                    {itemLabel}
                </Typography>
                <Typography sx={{fontSize: '13px', fontWeight: 700, color: '#111827', lineHeight: 1.3}}>
                    {name}
                </Typography>
            </Box>
            {suppressed ? <Box sx={{gridColumn: '2 / span 3'}}><SuppressedValue/></Box> : (<>
            <Box sx={{height: '12px', backgroundColor: 'var(--brand-surface-muted, #F3F4F6)', borderRadius: '6px', position: 'relative'}}>
                <Tooltip title={fmtValue(score)} arrow placement="top">
                    <Box sx={{position: 'absolute', left: 0, top: 0, bottom: 0, width: `${score ?? 0}%`, backgroundColor: rowColor, borderRadius: '6px', cursor: 'pointer'}}/>
                </Tooltip>
                {gradeAvg != null && (
                    <Tooltip title={benchmarkText('Grade avg', gradeAvg)} arrow placement="top">
                        <Box sx={primaryMarkerSx(gradeAvg)}/>
                    </Tooltip>
                )}
                {schoolAvg != null && (
                    <Tooltip title={benchmarkText('School avg', schoolAvg)} arrow placement="bottom">
                        <Box sx={secondaryMarkerSx(schoolAvg)}/>
                    </Tooltip>
                )}
            </Box>
            <Typography sx={{fontSize: '13px', fontWeight: 700, color: '#111827', textAlign: 'right'}}>{score ?? '—'}</Typography>
            {band ? <BandChip label={band} color={rowColor}/> : <Box/>}
            </>)}
        </Box>
    );
};

const ItemRowLegend = ({ gradeAvgName = 'Grade avg', schoolAvgName = 'School avg' }) => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap'}}>
        <BandLegendChips/>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `7px solid ${MARKER_PRIMARY}`}}/>
            <Typography sx={{fontSize: '11px', color: '#6A7282'}}>{gradeAvgName}</Typography>
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: `7px solid ${MARKER_SECONDARY}`}}/>
            <Typography sx={{fontSize: '11px', color: '#6A7282'}}>{schoolAvgName}</Typography>
        </Box>
    </Box>
);

/* Matches School Dashboard's ListCard exactly: white card, colored top bar,
   plain bulleted list (not a tinted left-border card). */
const InsightCard = ({title, items, color}) => (
    <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden', mb: 2.5}}>
        <Box sx={{backgroundColor: color, height: '6px'}}/>
        <Box sx={{p: 3}}>
            <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 1.5}}>{title}</Typography>
            <Box component="ul" sx={{m: 0, pl: 2.5}}>
                {items.map((item) => (
                    <Box component="li" key={item.name} sx={{mb: 1}}>
                        <Typography sx={{fontSize: '13.5px', color: '#374151', lineHeight: 1.5}}>
                            {item.name}{item.score === null || item.score === undefined ? '' : ` (${item.score})`}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    </Box>
);

/* Matches School Dashboard's ListCard exactly, for plain string items
   (rather than scored {name, score} items — see InsightCard above). */
const TipsCard = ({title, items, color}) => (
    <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden', mb: 2}}>
        <Box sx={{backgroundColor: color, height: '6px'}}/>
        <Box sx={{p: 2.5}}>
            <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY, mb: 1.2}}>{title}</Typography>
            <Box component="ul" sx={{m: 0, pl: 2.5}}>
                {items.map((it) => (
                    <Box component="li" key={it} sx={{mb: 0.7}}>
                        <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.5}}>{it}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    </Box>
);

const GuidanceBox = ({label = 'Decision guidance:', text}) => (
    <Box sx={{
        border: '1px solid #C7D9E8', borderLeft: `4px solid ${NAVY}`, borderRadius: '8px',
        backgroundColor: 'var(--brand-surface-subtle, #F0F6FB)', p: 2.5,
    }}>
        <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY, mb: 1}}>
            {label}
        </Typography>
        <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6}}>
            {text}
        </Typography>
    </Box>
);

const ItemProfileTab = ({
    title = 'Class Child Online Safety Readiness Item Profile',
    subtitle = 'Which Child Online Safety readiness item should I teach or reinforce?',
    scoresTitle = 'Readiness Item Scores',
    groupedAreas = [],
    // No sample defaults: the page always passes the class's own data.
    strengths = [],
    needsAttention = [],
    guidanceText = '',
    seriesName = 'Class Score',
    gradeAvgName = 'Grade avg',
    schoolAvgName = 'School avg'
}) => {
    const [sortMode, setSortMode] = useState('group');   // group order · highest first · lowest first (QA 9 Oct)
    const shownGroups = sortBars(groupedAreas, sortMode);
    return (
    <Box>
        <Typography sx={{fontSize: '23px', fontWeight: 800, color: NAVY, mb: 0.5, lineHeight: 1.2}}>
            {title}
        </Typography>
        <Typography sx={{fontSize: '14px', color: '#6A7282', fontStyle: 'italic', mb: 0.8}}>
            {subtitle}
        </Typography>

        <Grid container spacing={3} alignItems="flex-start">
            <Grid item xs={12} md={7}>
                <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3}}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 3}}>
                        <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>
                            {scoresTitle}
                        </Typography>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
                            <ItemRowLegend seriesName={seriesName} gradeAvgName={gradeAvgName} schoolAvgName={schoolAvgName}/>
                            <SortToggle value={sortMode} onChange={setSortMode}/>
                        </Box>
                    </Box>
                    {shownGroups.map((areaGroup, idx) => (
                        <Box key={areaGroup.parentLabel || 'all'} sx={{mb: idx < shownGroups.length - 1 ? 3.5 : 0}}>
                            {(areaGroup.areaTitle || areaGroup.parentLabel) && <Box sx={{mb: 2}}>
                                <Typography sx={{fontSize: '12px', color: '#6A7282', lineHeight: 1.3}}>
                                    {areaGroup.areaTitle}
                                </Typography>
                                <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY, lineHeight: 1.3}}>
                                    {areaGroup.parentLabel}
                                </Typography>
                            </Box>}
                            {areaGroup.items.map((it) => (
                                <ItemRow key={it.id} {...it} itemLabel={areaGroup.flattened && it.groupLabel ? `${it.itemLabel} · ${it.groupLabel}` : it.itemLabel}/>
                            ))}
                        </Box>
                    ))}
                </Box>
            </Grid>
            <Grid item xs={12} md={5}>
                <InsightCard title="Strengths"      items={strengths}      color="#1B6B55"/>
                <InsightCard title="Needs Attention" items={needsAttention} color="#E04535"/>
                <GuidanceBox text={guidanceText}/>
            </Grid>
        </Grid>
    </Box>
    );
};

/* ════════════════════════════════════════════════════════════════ */
/*  TAB 2 — ITEM DIAGNOSTICS (slide 4)                              */
/* ════════════════════════════════════════════════════════════════ */

const HOW_TO_USE = [
    'Each bar is a skill cluster from the item-level data.',
    'The navy tick is the grade’s mastery on the same cluster.',
    'Target the widest gaps, not the whole area.',
];

// A cluster under the n floor (suppressed) keeps its name and shows the placeholder in place of bar, figure and band.
const ClusterBar = ({name, pct, band, gradeMastery, suppressed, groupLabel}) => {
    const bandColor = READINESS_BAND_COLORS[band] || MASTERY_BAND_COLOR[band] || '#A5A5A5';
    return (
        <Box sx={{display: 'grid', gridTemplateColumns: '230px 1fr 44px 100px', alignItems: 'center', gap: 1.5, py: 0.9}}>
            <Box>
                <Typography sx={{fontSize: '13px', color: '#374151'}}>{name}</Typography>
                {/* In the value-sorted (flattened) list each row names its area. */}
                {groupLabel && <Typography sx={{fontSize: '11px', color: '#6A7282', lineHeight: 1.3}}>{groupLabel}</Typography>}
            </Box>
            {suppressed ? <Box sx={{gridColumn: '2 / span 3'}}><SuppressedValue/></Box> : (<>
            <Box sx={{height: '12px', backgroundColor: 'var(--brand-surface-muted, #F3F4F6)', borderRadius: '6px', position: 'relative'}}>
                <Tooltip title={fmtValue(pct, UNIT.PERCENT)} arrow placement="top">
                    <Box sx={{position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct ?? 0}%`, backgroundColor: bandColor, borderRadius: '6px', cursor: 'pointer'}}/>
                </Tooltip>
                {gradeMastery != null && (
                    <Tooltip title={benchmarkText('Grade avg', gradeMastery, UNIT.PERCENT)} arrow placement="top">
                        <Box sx={primaryMarkerSx(gradeMastery)}/>
                    </Tooltip>
                )}
            </Box>
            <Typography sx={{fontSize: '13px', fontWeight: 700, color: NAVY, textAlign: 'right'}}>{pct === null ? '—' : `${pct}%`}</Typography>
            {band ? <BandChip label={band} color={bandColor}/> : <Box/>}
            </>)}
        </Box>
    );
};

const ItemDiagnosticsTab = ({
    // No sample defaults: the page always passes the class's own data.
    clusterGroups = [],
    lessonFocus = [],
    recommendedAction = ''
}) => {
    // QA 9 Oct (C): weakest items first by default. Group order keeps the three
    // weakest-area headings with their items sorted by score ascending inside
    // each; the value modes rank every item in one list (suppressed last).
    const [sortMode, setSortMode] = useState('group')   // weakest-area groups kept, clusters weakest-first inside each (QA LA-CD-2);
    const shownGroups = sortMode === 'group'
        ? clusterGroups.map((grp) => ({...grp, clusters: sortItems(grp.clusters, 'asc')}))
        : sortBars(clusterGroups, sortMode, {itemsKey: 'clusters'});
    return (
        <Box>
            <Typography sx={{fontSize: '23px', fontWeight: 800, color: NAVY, mb: 0.5, lineHeight: 1.2}}>
                Readiness Items to Improve
            </Typography>
            <Typography sx={{fontSize: '14px', color: '#6A7282', fontStyle: 'italic', mb: 0.8}}>
                Which items explain the weakest areas?
            </Typography>

            <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs={12} md={7}>
                    <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3}}>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2}}>
                            <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY}}>
                                Readiness Items Behind the Weakest Areas
                            </Typography>

                            <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap'}}>
                                    <BandLegendChips/>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                        <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `7px solid ${MARKER_PRIMARY}`}}/>
                                        <Typography sx={{fontSize: '11px', color: '#6A7282'}}>Grade avg</Typography>
                                    </Box>
                                </Box>
                                <SortToggle value={sortMode} onChange={setSortMode}/>
                            </Box>
                        </Box>

                        {shownGroups.map((grp) => (
                            <Box key={grp.indicator || 'all'} sx={{mb: 2}}>
                                {grp.indicator && (
                                    <Typography sx={{fontSize: '11px', fontWeight: 700, color: ORANGE, letterSpacing: '0.5px', textTransform: 'uppercase', mt: 1.5, mb: 0.5}}>
                                        {grp.indicator}
                                    </Typography>
                                )}
                                {grp.clusters.map((c) => <ClusterBar key={c.name} {...c}/>)}
                            </Box>
                        ))}
                    </Box>
                </Grid>

                <Grid item xs={12} md={5}>
                    <TipsCard title="How to use this" items={HOW_TO_USE} color={NAVY}/>

                    <TipsCard title="This week's lesson focus" items={lessonFocus} color={ORANGE}/>

                    <GuidanceBox label="Recommended action:" text={recommendedAction}/>
                </Grid>
            </Grid>
        </Box>
    );
};

/* ════════════════════════════════════════════════════════════════ */
/*  MAIN COMPONENT                                                  */
/* ════════════════════════════════════════════════════════════════ */

const OVERVIEW_SECTION_CODE = 'S_2';
const READINESS_COMPETENCY_SECTION_CODE = 'S_3';
const ITEM_PROFILE_SECTION_CODE = 'S_4';

const CARD_SECTIONS = [
    OVERVIEW_SECTION_CODE,
    READINESS_COMPETENCY_SECTION_CODE,
    ITEM_PROFILE_SECTION_CODE,
];

const ReadinessSection = () => {
    const [activeTab, setActiveTab] = useState(0);
    const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTIONS });
    const dataS3 = dashboardData;
    const dataS2 = dashboardData;
    const loadingS3 = loading;
    const loadingS2 = loading;

    if (loading) {
        return (
            <ClassDashboardShell>
                <CircularLoader />
            </ClassDashboardShell>
        );
    }

    if (!dashboardData) {
        return (
            <ClassDashboardShell>
                <NoDataAvailable />
            </ClassDashboardShell>
        );
    }

    // Defensively locate section S_3
    let sectionS3 = null;
    if (dataS3.section_code === READINESS_COMPETENCY_SECTION_CODE) {
        sectionS3 = dataS3;
    } else if (dataS3.cards?.sections) {
        sectionS3 = dataS3.cards.sections.find(s => s.section_code === READINESS_COMPETENCY_SECTION_CODE);
    } else if (Array.isArray(dataS3.sections)) {
        sectionS3 = dataS3.sections.find(s => s.section_code === READINESS_COMPETENCY_SECTION_CODE);
    } else if (dataS3.cards && Array.isArray(dataS3.cards)) {
        sectionS3 = dataS3;
    }

    // Defensively locate section S_2
    let sectionS2 = null;
    if (dataS2.section_code === OVERVIEW_SECTION_CODE) {
        sectionS2 = dataS2;
    } else if (dataS2.cards?.sections) {
        sectionS2 = dataS2.cards.sections.find(s => s.section_code === OVERVIEW_SECTION_CODE);
    } else if (Array.isArray(dataS2.sections)) {
        sectionS2 = dataS2.sections.find(s => s.section_code === OVERVIEW_SECTION_CODE);
    } else if (dataS2.cards && Array.isArray(dataS2.cards)) {
        sectionS2 = dataS2;
    }

    if (!sectionS3 && !sectionS2) {
        return (
            <ClassDashboardShell>
                <NoDataAvailable />
            </ClassDashboardShell>
        );
    }

    const cardsS3 = sectionS3?.cards || [];
    const cardsS2 = sectionS2?.cards || [];

    // --- TAB 1 (ItemProfile) DATA PROCESSING ---
    // C_1: TitleCard (S_3)
    const titleCardS3 = cardsS3.find(c => c.card_type === 'TitleCard') || {};
    const titleText = titleCardS3.title || 'Class Child Online Safety Readiness Item Profile';
    const subTitleText = titleCardS3.footer || 'Which Child Online Safety readiness item should I teach or reinforce?';

    // C_2: BarChartBMCard (S_3)
    const chartCardS3 = cardsS3.find(c => c.card_type === 'BarChartBMCard') || {};
    const benchmarksS3 = chartCardS3.benchmarks || [];
    const seriesNameS3 = chartCardS3.series_name || 'Class Score';
    const gradeAvgNameS3 = (benchmarksS3.find(b => b.name?.toLowerCase().includes('grade'))?.name || 'Grade avg').replace(/\.$/, '');
    const schoolAvgNameS3 = (benchmarksS3.find(b => b.name?.toLowerCase().includes('school'))?.name || 'School avg').replace(/\.$/, '');

    // S_3's item list also carries the 4 Context & Support areas mixed in with
    // the 8 readiness areas — the Item Profile tab only shows readiness areas
    // (matches School Dashboard, where Context & Support lives on its own
    // section entirely), so filter to the canonical readiness area whitelist
    // before doing anything else with these items.
    const readinessItemsS3 = (chartCardS3.items || []).filter(item => {
        const parentLabel = (item.parent_label || '').trim().toLowerCase();
        return READINESS_AREA_NAMES.some(name => name.toLowerCase() === parentLabel);
    });

    const itemsS3 = readinessItemsS3.map((item, idx) => {
        const gradeAvgObj = item.benchmark_values?.find(b => b.name?.toLowerCase().includes('grade') || b.data?.token === 'grade');
        const schoolAvgObj = item.benchmark_values?.find(b => b.name?.toLowerCase().includes('school') || b.data?.token === 'school');
        const parentLabel = item.parent_label || 'Readiness Area';
        const areaIndex = READINESS_AREA_NAMES.findIndex(name => name.toLowerCase() === parentLabel.trim().toLowerCase());
        return {
            id: item.code || `COS ${String(idx + 1).padStart(2, '0')}`,
            itemLabel: '',                       // numbered below, 1…N straight through the areas (QA 9 Oct)
            areaTitle: `Readiness Area ${areaIndex >= 0 ? areaIndex + 1 : 1}`,
            parentLabel,
            areaIndex: areaIndex >= 0 ? areaIndex : READINESS_AREA_NAMES.length,
            name: item.label,
            suppressed: isSuppressedItem(item),                 // under the n floor: placeholder row
            score: isSuppressedItem(item) || item.value == null ? null : Math.round(item.value),
            band: isSuppressedItem(item) ? null : item.band,
            gradeAvg: gradeAvgObj && gradeAvgObj.value != null ? gradeAvgObj.value : null,
            schoolAvg: schoolAvgObj && schoolAvgObj.value != null ? schoolAvgObj.value : null,
        };
    });

    // Group by readiness area, then pin to the canonical fixed order (not a
    // rank by score) — same intent as School Dashboard's area grouping.
    const areasMapS3 = new Map();
    itemsS3.forEach((it) => {
        if (!areasMapS3.has(it.parentLabel)) {
            areasMapS3.set(it.parentLabel, {areaTitle: it.areaTitle, parentLabel: it.parentLabel, areaIndex: it.areaIndex, items: []});
        }
        areasMapS3.get(it.parentLabel).items.push(it);
    });
    const groupedAreasS3 = Array.from(areasMapS3.values()).sort((a, b) => a.areaIndex - b.areaIndex);
    // Items are numbered in the order shown, continuing across areas ("Readiness Item 1 … 30"), whatever the card's own sublabel says.
    let itemNo = 0;
    groupedAreasS3.forEach((g) => g.items.forEach((it) => { itemNo += 1; it.itemLabel = `Readiness Item ${itemNo}`; }));

    // Rankings run over the items that show a score (items under the n floor take no part);
    // when none does, the lists say why.
    const scoredS3 = itemsS3.filter((it) => it.score !== null);
    const noneShownS3 = itemsS3.length > 0 && scoredS3.length === 0 ? [{name: SUPPRESSED_COPY, score: null}] : [];
    // Strengths: top 3 (readiness items only)
    const strengths = scoredS3.length ? [...scoredS3]
        .sort((a, b) => b.score - a.score)
        .slice(0, 3)
        .map(it => ({name: it.name, score: it.score})) : noneShownS3;

    // Needs Attention: bottom 3 (readiness items only)
    const needsAttention = scoredS3.length ? [...scoredS3]
        .sort((a, b) => a.score - b.score)
        .slice(0, 3)
        .map(it => ({name: it.name, score: it.score})) : noneShownS3;

    // Guidance text (readiness items only)
    const sortedItemsS3 = [...scoredS3].sort((a, b) => a.score - b.score);
    const lowestItem = sortedItemsS3[0];
    const isBelowGrade = lowestItem && lowestItem.gradeAvg != null && lowestItem.score < lowestItem.gradeAvg;
    const guidanceText = lowestItem
        ? `Teach or reinforce ${lowestItem.name} (${lowestItem.score}) first — it is the class’s lowest item${isBelowGrade ? ' and below the grade' : ''}. Use the class strengths to set up peer support for the weaker clusters.`
        : (itemsS3.length ? 'Item scores are not shown for this class — fewer than 10 students completed the assessment. Raise participation before choosing what to teach first.' : '');

    // --- TAB 2 (ItemDiagnostics) DATA PROCESSING ---
    // C_5: BarChartBMCard (S_2) - Areas
    const chartCardS2 = cardsS2.find(c => c.card_type === 'BarChartBMCard') || {};
    const s2Items = chartCardS2.items || [];
    const s3Items = chartCardS3.items || [];

    const getAreaForS3Item = (s3Item, s2Items) => {
        if (!s3Item.code) return null;
        const partsS3 = s3Item.code.split('.');
        return s2Items.find(s2Item => {
            if (!s2Item.code) return false;
            const partsS2 = s2Item.code.split('.');
            if (partsS2.length > partsS3.length) return false;
            return partsS2.every((part, idx) => partsS3[idx] === part);
        });
    };

    const groupsMap = {};
    s3Items.forEach(s3Item => {
        const areaObj = getAreaForS3Item(s3Item, s2Items);
        const areaCode = areaObj ? areaObj.code : (s3Item.code ? s3Item.code.split('.').slice(0, -1).join('.') : 'Unknown');
        const areaLabel = areaObj ? areaObj.label : 'Other Indicators';
        const areaValue = areaObj && !isSuppressedItem(areaObj) && areaObj.value != null ? Math.round(areaObj.value) : null;

        if (!groupsMap[areaCode]) {
            groupsMap[areaCode] = {
                areaCode,
                label: areaLabel,
                value: areaValue,
                items: []
            };
        }
        groupsMap[areaCode].items.push(s3Item);
    });

    // Sort groups by Area score ascending (weakest first), then trim to the
    // top 3 weakest areas — matches the "Needs Attention" areas shown on the
    // Executive Overview page.
    const sortedGroups = Object.values(groupsMap)
        .sort((a, b) => (a.value ?? 101) - (b.value ?? 101));
    const weakestGroups = sortedGroups.slice(0, 3);

    const clusterGroups = weakestGroups.map(grp => {
        return {
            indicator: grp.value === null ? grp.label : `${grp.label} (${grp.value})`,
            clusters: grp.items.map(s3Item => {
                const suppressed = isSuppressedItem(s3Item);            // under the n floor: placeholder row
                const pct = suppressed || s3Item.value == null ? null : Math.round(s3Item.value);
                const gradeAvgObj = s3Item.benchmark_values?.find(b => b.name?.toLowerCase().includes('grade') || b.data?.token === 'grade');
                return {
                    name: s3Item.label,
                    suppressed,
                    pct: pct,
                    band: suppressed ? null : (s3Item.band || null),
                    gradeMastery: gradeAvgObj && gradeAvgObj.value != null ? Math.round(gradeAvgObj.value) : null
                };
            })
        };
    });

    // Collect weakest clusters/items across only the top 3 weakest areas for lesson focus
    const allItemsFromWeakestGroups = [];
    weakestGroups.forEach(grp => {
        grp.items.filter((s3Item) => !isSuppressedItem(s3Item) && s3Item.value != null).forEach(s3Item => {
            const gradeAvgObj = s3Item.benchmark_values?.find(b => b.name?.toLowerCase().includes('grade') || b.data?.token === 'grade');
            const gradeAvg = gradeAvgObj && gradeAvgObj.value != null ? gradeAvgObj.value : null;
            const diff = gradeAvg != null ? Math.round(s3Item.value - gradeAvg) : null;
            allItemsFromWeakestGroups.push({
                label: s3Item.label,
                value: Math.round(s3Item.value),
                diff
            });
        });
    });

    const lessonFocus = allItemsFromWeakestGroups
        .sort((a, b) => a.value - b.value)
        .slice(0, 3)
        .map(item => {
            let diffText = '';
            if (item.diff !== null) {
                if (item.diff === 0) {
                    diffText = ' · same as grade';
                } else {
                    diffText = ` · ${item.diff > 0 ? '+' : ''}${item.diff} vs grade`;
                }
            }
            return `${item.label} (${item.value}%${diffText})`;
        });

    const weakestTwo = allItemsFromWeakestGroups
        .sort((a, b) => a.value - b.value)
        .slice(0, 2);
    // Data Map CD-11-01 / -02: the datahouse sends both of these directly —
    // CD.S_4.C_2 (items of the 3 weakest areas, with parent area + grade avg) and
    // C_3 (this week's lesson focus, with the gap vs grade). They take precedence;
    // the derivation above stays as the fallback for a class without S_4.
    const s4Cards = (dashboardData.cards?.sections || []).find(s => s.section_code === ITEM_PROFILE_SECTION_CODE)?.cards || [];
    const s4Items = s4Cards.find(c => c.card_code === 'C_2')?.items || [];
    const s4Focus = s4Cards.find(c => c.card_code === 'C_3')?.items || [];
    const gradeOf = (it) => it.benchmark_values?.find(b => b.name?.toLowerCase().includes('grade') || b.data?.token === 'grade')?.value;
    const liveClusterGroups = [];
    s4Items.forEach((it) => {
        const key = it.parent_label || 'Readiness Area';
        let grp = liveClusterGroups.find(g => g.key === key);
        if (!grp) {
            grp = {key, indicator: it.parent_value != null ? `${key} (${Math.round(it.parent_value)})` : key, clusters: []};
            liveClusterGroups.push(grp);
        }
        const g = gradeOf(it);
        const suppressed = isSuppressedItem(it);                        // under the n floor: placeholder row
        grp.clusters.push({name: it.label, suppressed, pct: suppressed || it.value == null ? null : Math.round(it.value), band: suppressed ? null : (it.band || null), gradeMastery: g != null ? Math.round(g) : null});
    });
    // The lesson focus names only items that show a score.
    const liveFocusItems = s4Focus.filter((it) => !isSuppressedItem(it) && it.value != null).map((it) => {
        const g = gradeOf(it);
        const diff = it.data?.gap != null ? Math.round(it.data.gap) : (g != null ? Math.round(it.value - g) : null);
        return {label: it.label, value: Math.round(it.value), diff};
    });
    const liveLessonFocus = liveFocusItems.slice(0, 3).map((item) => {
        const diffText = item.diff === null ? '' : (item.diff === 0 ? ' · same as grade' : ` · ${item.diff > 0 ? '+' : ''}${item.diff} vs grade`);
        return `${item.label} (${item.value}%${diffText})`;
    });
    const focusTwo = liveFocusItems.length ? liveFocusItems.slice(0, 2) : weakestTwo;

    const recommendedAction = focusTwo.length >= 2
        ? `Build next week’s lesson around the two weakest clusters — ${focusTwo[0].label.toLowerCase()} and ${focusTwo[1].label.toLowerCase()} — rather than the whole indicator. Re-check these clusters at the next assessment.`
        : 'Build next week’s lesson around the weakest clusters rather than the whole indicator. Re-check these clusters at the next assessment.';

    return (
        <ClassDashboardShell
            tabs={
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    TabIndicatorProps={{style: {backgroundColor: ORANGE}}}
                    sx={{
                        borderBottom: '1px solid #E5E7EB', mb: 3,
                        '& .MuiTab-root': {textTransform: 'none', fontWeight: 600, fontSize: '14px', color: '#6A7282', px: 2},
                        '& .Mui-selected': {color: `${ORANGE} !important`},
                    }}
                >
                    <Tab label="Item Profile"/>
                    <Tab label="Item Level Diagnostics"/>
                </Tabs>
            }
        >
            {activeTab === 0 && (
                <ItemProfileTab
                    title={titleText}
                    subtitle={subTitleText}
                    scoresTitle={chartCardS3?.title || 'Readiness Item Scores'}
                    groupedAreas={groupedAreasS3}
                    strengths={strengths}
                    needsAttention={needsAttention}
                    guidanceText={guidanceText}
                    seriesName={seriesNameS3}
                    gradeAvgName={gradeAvgNameS3}
                    schoolAvgName={schoolAvgNameS3}
                />
            )}
            {activeTab === 1 && (
                <ItemDiagnosticsTab
                    clusterGroups={liveClusterGroups.length ? liveClusterGroups : clusterGroups}
                    lessonFocus={liveLessonFocus.length ? liveLessonFocus : lessonFocus}
                    recommendedAction={recommendedAction}
                />
            )}
        </ClassDashboardShell>
    );
};

export default ReadinessSection;
